/**
 * ServiceTask create result classification.
 * postRequest attaches non-enumerable __httpStatus when the JSON body is an object or array.
 * Primitives are returned unchanged. HTTP 4xx/5xx is never treated as offline. There is no offline queue.
 */

export const SERVICE_TASK_HTTP_STATUS_KEY = '__httpStatus';

export function attachHttpStatus(body, httpStatus) {
  if (body !== null && typeof body === 'object') {
    try {
      Object.defineProperty(body, SERVICE_TASK_HTTP_STATUS_KEY, {
        value: httpStatus,
        enumerable: false,
        configurable: true,
        writable: false,
      });
    } catch (_e) {
      // ignore frozen / sealed bodies
    }
  }
  return body;
}

export const SERVICE_TASK_CREATE_MESSAGES = Object.freeze({
  success: 'ServiceTask.RecordSaved',
  validation: 'ServiceTask.Create.ValidationFailed',
  permission: 'ServiceTask.Create.PermissionDenied',
  server: 'ServiceTask.ActionFailed',
  network: 'ServiceTask.Create.NotSaved',
  offline: 'ServiceTask.Create.OfflineNotSaved',
});

export function getServiceTaskHttpStatus(result) {
  if (result == null || typeof result !== 'object') return null;
  const attached = result[SERVICE_TASK_HTTP_STATUS_KEY];
  return typeof attached === 'number' ? attached : null;
}

export function firstValidationDetail(errors, fallback) {
  if (!errors || typeof errors !== 'object') return fallback;
  for (const value of Object.values(errors)) {
    if (Array.isArray(value) && value[0]) return String(value[0]);
    if (typeof value === 'string' && value !== '') return value;
  }
  return fallback;
}

export function interpretServiceTaskCreateResponse(result) {
  if (result == null) {
    return {
      ok: false,
      kind: 'network',
      messageKey: SERVICE_TASK_CREATE_MESSAGES.network,
      detail: null,
    };
  }
  const httpStatus = getServiceTaskHttpStatus(result);
  const bodyStatus = typeof result.status === 'number' ? result.status : null;
  const hasErrors = Boolean(result.errors && typeof result.errors === 'object' && !Array.isArray(result.errors));
  const successBody = bodyStatus === 201 || bodyStatus === 200;
  const successHttp = httpStatus == null || (httpStatus >= 200 && httpStatus < 300);

  if (successHttp && successBody) {
    return { ok: true, kind: 'success', httpStatus: httpStatus ?? bodyStatus, messageKey: SERVICE_TASK_CREATE_MESSAGES.success };
  }

  const status = httpStatus ?? bodyStatus;
  if (status === 422 || hasErrors) {
    return {
      ok: false,
      kind: 'validation',
      httpStatus: status ?? 422,
      messageKey: SERVICE_TASK_CREATE_MESSAGES.validation,
      detail: firstValidationDetail(result.errors, null),
      fieldErrors: hasErrors ? result.errors : {},
    };
  }
  if (status === 403) {
    return { ok: false, kind: 'permission', httpStatus: 403, messageKey: SERVICE_TASK_CREATE_MESSAGES.permission };
  }
  if (status === 401 || status === 419) {
    return { ok: false, kind: 'auth', httpStatus: status, messageKey: SERVICE_TASK_CREATE_MESSAGES.server };
  }
  if (status === 429 || (status != null && status >= 500) || bodyStatus === 400) {
    return { ok: false, kind: 'server', httpStatus: status, messageKey: SERVICE_TASK_CREATE_MESSAGES.server };
  }
  return { ok: false, kind: 'server', httpStatus: status, messageKey: SERVICE_TASK_CREATE_MESSAGES.server };
}

export function interpretServiceTaskCreateTransportError(isOnline) {
  if (isOnline === false) {
    return { ok: false, kind: 'offline', messageKey: SERVICE_TASK_CREATE_MESSAGES.offline };
  }
  return { ok: false, kind: 'network', messageKey: SERVICE_TASK_CREATE_MESSAGES.network };
}

export function appendServiceTaskSenderId(formData, user, values) {
  let senderId = null;
  if (values?.contractor_user) {
    senderId = user?.employee?.id ?? null;
  } else {
    senderId = values?.sender_id?.id ?? values?.sender_id ?? null;
  }
  if (senderId == null || senderId === '' || senderId === 0) {
    formData.append('sender_id', '');
    return '';
  }
  formData.append('sender_id', String(senderId));
  return String(senderId);
}
